import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, MagnifyingGlass, ArrowsDownUp, CloudArrowDown } from '@phosphor-icons/react';
import { toast } from 'sonner';

const VENDOR_CATEGORIES = ["Supplier","Service Provider","Contractor","Utility Provider","Government Agency","Consultant","Technology","Marketing Agency","Travel Agency","Other"];

export default function VendorsPage() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [showForm, setShowForm] = useState(false);
  const [showSAP, setShowSAP] = useState(false);
  const [editVendor, setEditVendor] = useState(null);

  const fetchVendors = () => {
    setLoading(true);
    const params = { limit: 100 };
    if (statusFilter !== 'all') params.status = statusFilter;
    if (search) params.search = search;
    api.get('/vendors', { params }).then(r => { setVendors(r.data.items); setTotal(r.data.total); }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchVendors(); }, [search, statusFilter]);

  return (
    <div className="space-y-4" data-testid="vendors-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Vendors</h1>
        {user?.role === 'ap_accounting' && (
          <div className="flex gap-2">
            <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0043CC] gap-2" onClick={() => { setEditVendor(null); setShowForm(true); }} data-testid="add-vendor-btn">
              <Plus size={16} /> Add Vendor
            </Button>
            <Button variant="outline" className="rounded-sm gap-2" onClick={() => setShowSAP(true)} data-testid="sap-sync-btn">
              <CloudArrowDown size={16} /> Get from SAP
            </Button>
          </div>
        )}
      </div>

      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendors..." className="pl-9 rounded-sm h-9" data-testid="vendor-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] rounded-sm h-9" data-testid="vendor-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-[#475569]">{total} vendors</span>
      </div>

      <div className="border rounded-sm bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F1F5F9]">
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Code</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Name</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Category</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Contact</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Source</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={6}><div className="h-4 bg-muted rounded animate-pulse" /></TableCell></TableRow>)
            ) : vendors.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-[#94A3B8] py-8">No vendors found</TableCell></TableRow>
            ) : (
              vendors.map(v => (
                <TableRow key={v.id} className="hover:bg-[#F8FAFC]">
                  <TableCell className="text-sm font-mono">{v.vendor_code}</TableCell>
                  <TableCell><Link to={`/vendors/${v.id}`} className="text-[#0055FF] hover:underline font-medium text-sm" data-testid={`vendor-link-${v.vendor_code}`}>{v.name}</Link></TableCell>
                  <TableCell className="text-sm">{v.category}</TableCell>
                  <TableCell className="text-sm text-[#475569]">{v.contact_person}</TableCell>
                  <TableCell><span className={`text-[10px] uppercase tracking-[0.05em] font-semibold px-1.5 py-0.5 rounded-sm ${v.source === 'sap' ? 'bg-[#DCFCE7] text-[#166534]' : 'bg-[#F1F5F9] text-[#475569]'}`}>{v.source || 'manual'}</span></TableCell>
                  <TableCell><span className={`text-[10px] uppercase tracking-[0.05em] font-semibold px-1.5 py-0.5 rounded-sm ${v.status === 'active' ? 'bg-[#DCFCE7] text-[#166534]' : 'bg-[#FEE2E2] text-[#991B1B]'}`}>{v.status}</span></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Vendor Form Dialog */}
      {showForm && <VendorFormDialog open={showForm} onClose={() => setShowForm(false)} vendor={editVendor} onSaved={fetchVendors} />}

      {/* SAP Search Dialog */}
      {showSAP && <SAPSearchDialog open={showSAP} onClose={() => setShowSAP(false)} onImported={fetchVendors} />}
    </div>
  );
}

function VendorFormDialog({ open, onClose, vendor, onSaved }) {
  const [form, setForm] = useState({ name: '', category: '', contact_person: '', contact_number: '', email: '', billing_address: '', tax_id: '', payment_terms: '', notes: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (vendor) setForm({ name: vendor.name, category: vendor.category || '', contact_person: vendor.contact_person || '', contact_number: vendor.contact_number || '', email: vendor.email || '', billing_address: vendor.billing_address || '', tax_id: vendor.tax_id || '', payment_terms: vendor.payment_terms || '', notes: vendor.notes || '' });
    else setForm({ name: '', category: '', contact_person: '', contact_number: '', email: '', billing_address: '', tax_id: '', payment_terms: '', notes: '' });
  }, [vendor, open]);

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Vendor name is required'); return; }
    setLoading(true);
    try {
      if (vendor) {
        await api.put(`/vendors/${vendor.id}`, form);
        toast.success('Vendor updated');
      } else {
        await api.post('/vendors', form);
        toast.success('Vendor created');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{vendor ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Vendor Name</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="rounded-sm mt-1" data-testid="vendor-name-input" /></div>
          <div><Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Category</Label>
            <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
              <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent className="rounded-sm">{VENDOR_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Contact Person</Label><Input value={form.contact_person} onChange={e => setForm(f => ({ ...f, contact_person: e.target.value }))} className="rounded-sm mt-1" /></div>
            <div><Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Contact Number</Label><Input value={form.contact_number} onChange={e => setForm(f => ({ ...f, contact_number: e.target.value }))} className="rounded-sm mt-1" /></div>
          </div>
          <div><Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Email</Label><Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="rounded-sm mt-1" /></div>
          <div><Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Billing Address</Label><Input value={form.billing_address} onChange={e => setForm(f => ({ ...f, billing_address: e.target.value }))} className="rounded-sm mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Tax ID / TIN</Label><Input value={form.tax_id} onChange={e => setForm(f => ({ ...f, tax_id: e.target.value }))} className="rounded-sm mt-1" /></div>
            <div><Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Payment Terms</Label><Input value={form.payment_terms} onChange={e => setForm(f => ({ ...f, payment_terms: e.target.value }))} className="rounded-sm mt-1" /></div>
          </div>
          <div><Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="rounded-sm mt-1" rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0043CC]" onClick={handleSubmit} disabled={loading} data-testid="save-vendor-btn">{loading ? 'Saving...' : 'Save Vendor'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SAPSearchDialog({ open, onClose, onImported }) {
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('name');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post('/vendors/sap-search', { query, search_type: searchType });
      setResults(data.results);
    } catch (err) {
      toast.error('SAP search failed');
    } finally { setLoading(false); }
  };

  const handleImport = async (vendor) => {
    setImporting(true);
    try {
      await api.post('/vendors/sap-import', vendor);
      toast.success(`Vendor ${vendor.name} imported from SAP`);
      onImported();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-2xl">
        <DialogHeader><DialogTitle className="font-heading">Search SAP Vendors</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          <Select value={searchType} onValueChange={setSearchType}>
            <SelectTrigger className="w-[140px] rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-sm"><SelectItem value="name">Name</SelectItem><SelectItem value="code">Code</SelectItem><SelectItem value="tax_id">Tax ID</SelectItem></SelectContent>
          </Select>
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search SAP..." className="rounded-sm" onKeyDown={e => e.key === 'Enter' && handleSearch()} data-testid="sap-search-input" />
          <Button className="rounded-sm bg-[#0055FF]" onClick={handleSearch} disabled={loading} data-testid="sap-search-btn">{loading ? 'Searching...' : 'Search'}</Button>
        </div>
        {results.length > 0 && (
          <Table>
            <TableHeader><TableRow><TableHead className="text-[10px] uppercase tracking-[0.1em]">SAP Code</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]">Name</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]">Category</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {results.map(r => (
                <TableRow key={r.sap_code}>
                  <TableCell className="text-sm font-mono">{r.sap_code}</TableCell>
                  <TableCell className="text-sm font-medium">{r.name}</TableCell>
                  <TableCell className="text-sm">{r.category}</TableCell>
                  <TableCell><Button size="sm" className="rounded-sm bg-[#22C55E] hover:bg-[#16A34A]" onClick={() => handleImport(r)} disabled={importing} data-testid={`import-${r.sap_code}`}>Import</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {results.length === 0 && query && !loading && <p className="text-sm text-[#94A3B8] text-center py-4">No results found. Try a different search term.</p>}
        <p className="text-[10px] text-[#94A3B8] uppercase tracking-[0.1em]">Source: SAP Integration (Mock)</p>
      </DialogContent>
    </Dialog>
  );
}
